<template>
  <v-dialog v-model="visible" max-width="900px" persistent>
    <!-- FORM DIALOG -->
    <template v-if="dialogState === 'form'">
      <v-card class="container-card">
        <h2>{{ $t('homePage.generationAIDialog.formTitle') }}</h2>
        <v-form ref="form" @submit.prevent="handleSubmit" class="container-form">
          <v-text-field
            v-model="form.place"
            :rules="rules.required"
            :label="$t('homePage.generationAIDialog.placeLabel')"
            :placeholder="$t('homePage.generationAIDialog.placePlaceholder')"
            clearable
          ></v-text-field>
          <v-text-field
            v-model="form.time"
            :label="$t('homePage.generationAIDialog.timeLabel')"
            :placeholder="$t('homePage.generationAIDialog.timePlaceholder')"
            clearable
          ></v-text-field>
          <v-text-field
            v-model="form.theme"
            :label="$t('homePage.generationAIDialog.themeLabel')"
            :placeholder="$t('homePage.generationAIDialog.themePlaceholder')"
            clearable
          ></v-text-field>
          <v-layout class="container-buttons">
            <v-btn color="primary" @click="closeDialog">{{ $t('homePage.generationAIDialog.cancelButton') }}</v-btn>
            <v-btn color="primary" type="submit">{{ $t('homePage.generationAIDialog.submitButton') }}</v-btn>
          </v-layout>
        </v-form>
      </v-card>
    </template>

    <!-- LOADING DIALOG -->
    <template v-else-if="dialogState === 'loading'">
      <v-card class="container-card">
        <h2>{{ $t('homePage.generationAIDialog.loadingTitle') }}</h2>
        <lottie v-if="loadingUrl" :path="loadingUrl" :loop="true" :height="250"></lottie>
        <p>{{ $t('homePage.generationAIDialog.loadingText') }}</p>
      </v-card>
    </template>

    <template v-else-if="dialogState === 'success'">
      <!-- SUCCESS DIALOG -->
      <v-card class="container-card">
        <h2>{{ $t('homePage.generationAIDialog.successTitle') }}</h2>
        <div class="container-message-end">
          <p v-html="$t('homePage.generationAIDialog.successMessage', [currentUser.username])"/>
          <small><a>{{ $t('homePage.generationAIDialog.notMyEmail') }}</a></small>
        </div>
        <div>
          <p>{{ $t('homePage.generationAIDialog.successPreview') }}</p>
          <div v-if="isIntriguesParsed" class="container-intrigues">
            <div v-for="(intrigue, index) in intrigues" :key="index" class="container-bloc">
              <h4> {{ $t('homePage.generationAIDialog.titleScenario', [isTitlesParsed ? titles[index] : index + 1]) }}</h4>
              <p>{{ intrigue }}</p>
            </div>
          </div>
          <div v-else class="container-bloc">
            <p>{{ intrigues }}</p>
          </div>
        </div>
        <v-btn color="primary" @click="closeDialog">{{ $t('homePage.generationAIDialog.returnHome') }}</v-btn>
      </v-card>
    </template>

    <!-- FAIL DIALOG -->
    <template v-else-if="dialogState === 'failure'">
      <v-card class="container-card">
        <h2>{{ $t('homePage.generationAIDialog.failureTitle') }}</h2>
        <p>{{ $t('homePage.generationAIDialog.failureMessage') }}</p>
        <v-btn color="primary" @click="closeDialog">{{ $t('homePage.generationAIDialog.retryLater') }}</v-btn>
      </v-card>
    </template>
  </v-dialog>
</template>

<script>
  import Lottie from 'lottie-vuejs/src/LottieAnimation.vue';
  import LottieAnimation from 'lottie-vuejs/src/LottieAnimation.vue';

  export default {
    name: 'GenerationAiDialog',
    components: {
      Lottie,
      LottieAnimation,
    },
    props: {
      visible: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        loadingUrl: 'images/idea_is_coming.json',
        titles: [],
        isTitlesParsed: false,
        intrigues: [],
        isIntriguesParsed: false,
        dialogVisible: false,
        dialogState: 'form',
        form : {
          place: '',
          theme: '',
          time: '',
        },
        rules: {
          required: [v => !!v || 'Ce champ est requis']
        },
      };
    },
    computed: {
      currentUser() {
        return this.$store.getters['auth/currentUser'];
      },
    },
    methods: {
      async handleSubmit() {
        if (!this.$refs.form.validate()) {
          return;
        }
        // DEV MODE
        if (this.form.place === 'e') {
          this.$refs.form.reset();
          this.dialogState = 'failure';
          return;
        } else if (this.form.place === 'd') {
          this.form.place = 'Se situe en France';
          this.form.time = 'A l\'époque des hommes préhistoriques';
          this.form.theme = 'Sur le thème des inventions faites par les hommes préhistoriques';
        } else if (this.form.place === 's') {
          this.$refs.form.reset();
          this.dialogState = 'success';
          return;
        }
        const [place, time, theme] = [this.form.place, this.form.time, this.form.theme];
        console.log('Place:', place, 'Time period:', time, 'Theme:', theme);
        this.$refs.form.reset();
        this.dialogState = 'loading';
        const response = await this.askForAGeneration(place, time, theme);
        this.titles = this.parseTitles(response);
        this.intrigues = this.parseIntrigues(response);
        if (this.dialogState !== 'failure' && this.intrigues.length > 0) {
          this.dialogState = 'success';
        }
      },
      closeDialog() {
        this.$emit('update:visible', false);
        setTimeout(() => {
          this.dialogState = 'form';
          if (this.$refs.form) {
            this.$refs.form.reset();
          }
        }, 500);
      },
      async askForAGeneration(place, time, theme) {
        try {
          console.log('Asking for a generation...');
          const response = await this.$store.dispatch('paths/ASK_FOR_A_GENERATION', {
            place: place,
            time: time,
            theme: theme,
            email: this.currentUser.username,
            kuzzle: this.$kuzzle,
          });
          if (response.result.scenarios) {
            //console.log('Generation received:', response.result.scenarios);
            return response;
          } else {
            this.dialogState = 'failure';
            console.error('No generation received:', response);
            this.$toast.show(
              this.$t('homePage.generationAIDialog.errorAskForAGeneration'),
            );
          }
        } catch (error) {
          this.dialogState = 'failure';
          console.error(error.message);
          this.$toast.show(
            this.$t('homePage.generationAIDialog.errorAskForAGeneration'),
          );
        }
      },
      determineSplitterWord(text, splitterWords) {
        for (const splitterWord of splitterWords) {
          if (text.includes(splitterWord)) {
            return splitterWord;
          }
        }
        return '';
      },
      parseTitles(response) {
        this.isTitlesParsed = false;
        const scenarios = response.result.scenarios;
        let splitter = this.determineSplitterWord(scenarios, ['Scénario', '# ', '**']);
        if (splitter === '') {
          // console.log('No splitter found in: ', scenarios);
          return '';
        }
        let titlesParsed = scenarios.split(splitter).slice(1).map(scenario => {
          if (scenario.includes('**') && scenario.indexOf('**') < scenario.indexOf('\n')) {
            // console.log('Scenario with **: ', scenario);
            return scenario.split('**')[0].trim();
          } else if (scenario.includes('\n')) {
            // console.log('Scenario with \\n: ', scenario);
            return scenario.split('\n')[0].trim();
          }
        });

        if (titlesParsed.length !== 3) {
          // console.log('Titles NOT parsed: ', scenarios);
          return '';
        }
        // console.log('Titles parsed: ', titlesParsed);
        this.isTitlesParsed = true;
        return titlesParsed;
      },
      parseIntrigues(response) {
        this.isIntriguesParsed = false;
        this.intrigues = [];
        const scenarios = response.result.scenarios;
        let splitter = this.determineSplitterWord(scenarios, ['Intrigue Principale:', 'Intrigue Principale :', 'Intrigue Principale\n', 'Intrigue Principale**:', 'Intrigue Principale** :', 'Intrigue Principale**\n']);
        if (splitter === '') {
          // console.log('No splitter found in: ', scenarios);
          return scenarios;
        }
        let scenariosParsed = scenarios.split(splitter).slice(1).map(scenario => {
          // console.log('For each scenario (in map): ', scenario);
          if (scenario.startsWith('**')) {
            scenario = scenario.slice(2);
          }
          return scenario.split('\n')[0].trim();
        });

        if (scenariosParsed.length !== 3) {
          // console.log('Scenarios NOT parsed: ', scenarios);
          return scenarios;
        }
        // console.log('Scenarios parsed: ', scenariosParsed);
        this.isIntriguesParsed = true;
        return scenariosParsed;
      },
    },
  };
</script>

<style scoped>
  .container-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 20px;
    gap: 80px;
    padding: 50px 130px;
  }
  .container-form {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 50px;
  }
  .text-field >>> .v-label {
    font-weight: bold;
    font-size: 14px;
    text-align: center;
    top: -50%;
    left: 50% !important;
    transform: translateX(-50%);
    &.v-label--active {
      transform: translateY(-18px) scale(.75) translateX(-50%);
    }
  }
  .container-buttons {
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
  }
  .container-message-end {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    p {
      color: #4E4A4A;
      font-size: 16px;
      text-align: center;
    }
  }
  .container-intrigues {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }
  .container-bloc {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: #F3F3F3;
    padding: 16px;
    border-radius: 16px;
    gap: 10px;
    color: #3F3F3F;
    h4 {
      font-size: 16px;
      text-align: center;
    }
    p {
      font-size: 14px;
    }
  }
</style>
